import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Inv360VehicleObject } from '@lgg/isomorphic/types/__generated__/graphql';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import { Scrollbar } from 'src/components/general/display/scrollbar';
import { Checkbox } from 'src/components/general/inputs/checkbox';
import { Select } from 'src/components/general/inputs/select/select';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  DrawerBody,
  FooterRow,
  ScrollableSection,
  StickyFooter,
  Thumb,
  ThumbInfoWrapper,
  ToggleCircle,
  VehicleInfo,
  VehicleMeta,
  VehiclePrice,
  VehicleTitle,
} from './shared';

const ConfigureContent = styled(FlexColumn)`
  gap: 35px;
  padding: 20px;
`;

const SalespersonSection = styled(FlexColumn)`
  gap: 10px;
`;

const SalespersonTitleBlock = styled(FlexColumn)`
  gap: 5px;
`;

const VehiclesSection = styled(FlexColumn)`
  gap: 15px;
`;

const VehiclesList = styled(FlexColumn)`
  gap: 20px;
`;

const SectionTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 15px;
  line-height: 19px;
`;

const SectionDescription = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;
`;

const CheckboxRow = styled.div`
  margin-top: 5px;

  .ant-checkbox-wrapper span:not(.ant-checkbox) {
    color: ${({ theme }) => theme.colors.flint};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 13px;
    line-height: normal;
  }
`;

const SelectedVehicleRow = styled(FlexRow)`
  align-items: center;
  box-sizing: border-box;
  gap: 15px;
  height: 58px;

  ${Thumb} {
    height: 58px;
    width: 58px;
  }

  ${VehicleInfo} {
    gap: 3px;
  }
`;

const SelectedVehicleMetaRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 5px;
`;

const SelectedVehicleSeparator = styled.span`
  color: ${({ theme }) => theme.colors.geyser};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;
`;

const VehiclesEmptyState = styled.div`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  line-height: 18px;
  padding: 10px 0;
  text-align: center;
`;

const AddVehiclesButton = styled(ButtonV2)`
  height: 32px;
  padding: 7px 15px;
  width: 100%;

  > span:last-child {
    width: auto;
  }
`;

type Inv360ConfigureStepProps = {
  salespersonOptions: { value: string; label: string }[];
  selectedVehicles: Inv360VehicleObject[];
  removeVehicle: (id: number) => void;
  goToPicker: VoidFunction;
  salespersonId: number | null;
  setSalespersonId: (id: number | null) => void;
  rememberSalesperson: boolean;
  setRememberSalesperson: (value: boolean) => void;
  onGenerate: VoidFunction;
  isGenerating: boolean;
};

export const Inv360ConfigureStep = memo<Inv360ConfigureStepProps>(
  ({
    salespersonOptions,
    selectedVehicles,
    removeVehicle,
    goToPicker,
    salespersonId,
    setSalespersonId,
    rememberSalesperson,
    setRememberSalesperson,
    onGenerate,
    isGenerating,
  }) => {
    const { t } = useTranslation(['conversations']);

    const selectedVehicleItems = useMemo(
      () =>
        selectedVehicles.map((vehicle) => {
          const mileageDays = [
            vehicle.mileage
              ? t('conversations:messageInput.options.inv360.mileage', {
                  count: vehicle.mileage,
                })
              : null,
            vehicle.daysInInventory != null
              ? t('conversations:messageInput.options.inv360.daysInInventory', {
                  count: vehicle.daysInInventory,
                })
              : null,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <SelectedVehicleRow key={vehicle.id} data-lgg-id="inv360-selected-vehicle">
              <ThumbInfoWrapper>
                <Thumb src={vehicle.thumbnailUrl ?? undefined} alt={vehicle.title} />
                <VehicleInfo>
                  <VehicleTitle>{vehicle.title}</VehicleTitle>
                  <SelectedVehicleMetaRow>
                    <VehiclePrice>{vehicle.formattedPrice}</VehiclePrice>
                    {mileageDays ? (
                      <>
                        <SelectedVehicleSeparator>—</SelectedVehicleSeparator>
                        <VehicleMeta>{mileageDays}</VehicleMeta>
                      </>
                    ) : null}
                  </SelectedVehicleMetaRow>
                  {vehicle.vin ? <VehicleMeta>{vehicle.vin}</VehicleMeta> : null}
                </VehicleInfo>
              </ThumbInfoWrapper>
              <ToggleCircle
                $selected
                icon="remove"
                onClick={() => removeVehicle(vehicle.id)}
                aria-label="Remove"
                data-lgg-id="inv360-selected-vehicle-remove"
              />
            </SelectedVehicleRow>
          );
        }),
      [selectedVehicles, removeVehicle, t],
    );

    return (
      <DrawerBody>
        <ScrollableSection>
          <Scrollbar>
            <ConfigureContent>
              <SalespersonSection>
                <SalespersonTitleBlock>
                  <SectionTitle>
                    {t('conversations:messageInput.options.inv360.salesperson.title')}
                  </SectionTitle>
                  <SectionDescription>
                    {t(
                      'conversations:messageInput.options.inv360.salesperson.description',
                    )}
                  </SectionDescription>
                </SalespersonTitleBlock>
                <Select
                  name="inv360-salesperson-select"
                  value={
                    salespersonOptions.find(
                      (salesperson) => salesperson.value === String(salespersonId),
                    ) ?? null
                  }
                  options={salespersonOptions}
                  isSearchable={false}
                  placeholder={t(
                    'conversations:messageInput.options.inv360.salesperson.placeholder',
                  )}
                  mobileLabel={t(
                    'conversations:messageInput.options.inv360.salesperson.title',
                  )}
                  onChange={(option) =>
                    setSalespersonId(option ? Number(option.value) : null)
                  }
                  isClearable
                  forceCaret
                />
                <CheckboxRow>
                  <Checkbox
                    checked={rememberSalesperson}
                    onChange={(e) => setRememberSalesperson(e.target.checked)}
                    data-lgg-id="inv360-remember-salesperson"
                  >
                    {t('conversations:messageInput.options.inv360.salesperson.remember')}
                  </Checkbox>
                </CheckboxRow>
              </SalespersonSection>
              <VehiclesSection>
                <SectionTitle>
                  {t('conversations:messageInput.options.inv360.vehicles.title')}
                </SectionTitle>
                <VehiclesList>
                  <AddVehiclesButton
                    size="small"
                    variant="default"
                    icon="add"
                    onClick={goToPicker}
                    data-lgg-id="inv360-add-vehicles"
                  >
                    {t('conversations:messageInput.options.inv360.vehicles.addMore')}
                  </AddVehiclesButton>
                  {selectedVehicles.length === 0 && (
                    <VehiclesEmptyState data-lgg-id="inv360-selected-vehicles-empty">
                      {t('conversations:messageInput.options.inv360.vehicles.empty')}
                    </VehiclesEmptyState>
                  )}
                  {selectedVehicleItems}
                </VehiclesList>
              </VehiclesSection>
            </ConfigureContent>
          </Scrollbar>
        </ScrollableSection>
        <StickyFooter>
          <FooterRow>
            <ButtonV2
              size="regular"
              variant="default"
              onClick={goToPicker}
              data-lgg-id="inv360-back-button"
            >
              {t('conversations:messageInput.options.inv360.back')}
            </ButtonV2>
            <ButtonV2
              size="regular"
              variant="cta"
              disabled={isGenerating || !salespersonId || selectedVehicles.length === 0}
              onClick={onGenerate}
              data-lgg-id="inv360-generate-link-button"
            >
              {t('conversations:messageInput.options.inv360.generateLink')}
            </ButtonV2>
          </FooterRow>
        </StickyFooter>
      </DrawerBody>
    );
  },
);
